/**
 * Survey filters kept in the URL query string, so a filtered view can be
 * bookmarked, shared or reached from a survey page:
 *
 *   ?site=Pichavaram&site=Bhitarkanika&survey=3&from=2025-01-01&to=2025-12-31
 *
 * Sites match a survey's `location`; dates are ISO `YYYY-MM-DD` strings and
 * both ends are inclusive. Every filter that is set must match (AND).
 */

export const EMPTY_FILTERS = { sites: [], surveyIds: [], from: '', to: '' };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const filtersFromParams = (params) => ({
  sites: params.getAll('site').filter(Boolean),
  surveyIds: params
    .getAll('survey')
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0),
  from: ISO_DATE.test(params.get('from') || '') ? params.get('from') : '',
  to: ISO_DATE.test(params.get('to') || '') ? params.get('to') : '',
});

/** Writes the filters into `params` (a URLSearchParams), leaving other keys alone. */
export const filtersToParams = (filters, params = new URLSearchParams()) => {
  const next = new URLSearchParams(params);
  ['site', 'survey', 'from', 'to'].forEach((key) => next.delete(key));
  filters.sites.forEach((site) => next.append('site', site));
  filters.surveyIds.forEach((id) => next.append('survey', String(id)));
  if (filters.from) next.set('from', filters.from);
  if (filters.to) next.set('to', filters.to);
  return next;
};

export const hasFilters = (filters) =>
  filters.sites.length > 0 || filters.surveyIds.length > 0 || Boolean(filters.from) || Boolean(filters.to);

const inDateRange = (survey, { from, to }) => {
  const date = (survey.survey_date || '').slice(0, 10);
  if (from && (!date || date < from)) return false;
  if (to && (!date || date > to)) return false;
  return true;
};

const atSite = (survey, sites) => sites.length === 0 || sites.includes((survey.location || '').trim());

/** Surveys matching the site and date filters (ignoring any picked surveys). */
export const matchSiteAndDate = (surveys, filters) =>
  surveys.filter((s) => atSite(s, filters.sites) && inDateRange(s, filters));

/** Surveys matching every filter. */
export const applyFilters = (surveys, filters) => {
  const picked = new Set(filters.surveyIds);
  return matchSiteAndDate(surveys, filters).filter((s) => picked.size === 0 || picked.has(s.id));
};

/** Distinct, sorted site names. */
export const siteNames = (surveys) =>
  [...new Set(surveys.map((s) => (s.location || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));

/** Link to the dashboard filtered to a single survey. */
export const dashboardLinkForSurvey = (surveyId) => `/?${filtersToParams({ ...EMPTY_FILTERS, surveyIds: [surveyId] })}`;

// --- Date presets -------------------------------------------------------------

const pad = (n) => String(n).padStart(2, '0');
/** A local date as YYYY-MM-DD (not toISOString, which would use UTC and can be a day out). */
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Distinct survey years, newest first. */
export const surveyYears = (surveys) =>
  [...new Set(surveys.map((s) => Number((s.survey_date || '').slice(0, 4))).filter((y) => y > 0))].sort((a, b) => b - a);

/**
 * One-click date ranges for the filter bar: recent periods, then each other
 * year that has surveys. Each sets the From / To filters to fixed dates (so a
 * shared link keeps the same range).
 */
export const datePresets = (surveys, today = new Date()) => {
  const year = today.getFullYear();
  const daysAgo = (n) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - n);
  const yearRange = (y) => ({ from: `${y}-01-01`, to: `${y}-12-31` });
  const recent = [
    { key: 'last30', label: 'Last 30 days', from: isoDate(daysAgo(29)), to: isoDate(today) },
    { key: 'last12', label: 'Last 12 months', from: isoDate(new Date(year - 1, today.getMonth(), today.getDate() + 1)), to: isoDate(today) },
    { key: 'thisYear', label: `This year (${year})`, ...yearRange(year) },
    { key: 'lastYear', label: `Last year (${year - 1})`, ...yearRange(year - 1) },
  ];
  const years = surveyYears(surveys)
    .filter((y) => y !== year && y !== year - 1)
    .map((y) => ({ key: `year-${y}`, label: String(y), group: 'years', ...yearRange(y) }));
  return [...recent, ...years];
};

/** The preset the From / To filters currently match, if any. */
export const matchingPreset = (filters, presets) =>
  presets.find((p) => p.from === filters.from && p.to === filters.to) || null;
